import React, { useMemo, useRef, useState } from "react";
import Select from "react-select";
import { LuAlertCircle, LuPlus, LuTag, LuTrash2 } from "react-icons/lu";

import cx from "../ui/cx";
import {
  arrayAddButtonClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  formSelectIconClass,
} from "../ui/styles";
import { iconSelectStyles } from "../../static/styles";
import { CuttingCategoryProps } from "../../types/cuttingOrder";

// The category boxes of the cutting order and receive forms, like the reference app: each box picks one category, and
// its "નવું આઈટમ ઉમેરો" opens the picker on that category's items. The forms draw their own rows in the boxes, with the
// row classes below, so a receive row looks like an order row.

// One category box of a form: its category (null until one is picked) and the indexes of its lines in the form's
// items.
export type CuttingCategoryBox = {
  key: string;
  categoryId: number | null;
  indexes: number[];
};

export type CuttingCategoryOption = { value: number; label: string };

// "Cabinate Handle (CB)", as the item page lists categories.
export const categoryText = (category: CuttingCategoryProps) =>
  category.code ? `${category.name} (${category.code})` : category.name;

export const rowTitleClass =
  "md:col-span-12 flex items-center justify-between gap-2 text-sm font-semibold text-[#113F67]";

// Text the size of the other inputs (14px from md up, like ui/Input).
export const readOnlyClass = cx(
  fieldInputClass(),
  "flex items-center cursor-not-allowed bg-gray-100 md:text-sm"
);

// The row's field icons, the same in every box: 16px in from its edge and centred on its height (the forms' usual
// icon sits a little low). Inside the item button the icon is placed from inside its 2px border, so it starts 2px less
// in.
export const lineIconClass =
  "pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#34699A]";
export const itemIconClass =
  "pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#34699A]";

// A category box as the form keeps it: its category, null until one is picked.
type BoxState = { key: string; categoryId: number | null };

// The boxes of a form. A new form starts with one empty box; a saved one with a box per category of its lines, in line
// order (initialCategoryIds gives each saved line's category). lineCategoryIds gives each of the form's lines its
// category, in the form's order. A line whose category has no box (its item moved to another category) gets a box of
// its own at the end, so no line goes unseen.
export const useCategoryBoxes = (
  initialCategoryIds: () => number[],
  lineCategoryIds: number[],
  removeLines: (indexes: number[]) => void
) => {
  const [boxes, setBoxes] = useState<BoxState[]>(() => {
    const categoryIds = Array.from(new Set(initialCategoryIds()));

    return categoryIds.length > 0
      ? categoryIds.map((categoryId, index) => ({
          key: `box-${index}`,
          categoryId,
        }))
      : [{ key: "box-0", categoryId: null }];
  });
  const nextBoxKey = useRef(boxes.length);

  const categoryBoxes = useMemo((): CuttingCategoryBox[] => {
    const list: CuttingCategoryBox[] = boxes.map((box) => ({
      ...box,
      indexes: [],
    }));

    lineCategoryIds.forEach((categoryId, index) => {
      let box = list.find((item) => item.categoryId === categoryId);

      if (!box) {
        box = { key: `line-${categoryId}`, categoryId, indexes: [] };
        list.push(box);
      }

      box.indexes.push(index);
    });

    return list;
  }, [boxes, lineCategoryIds]);

  const addBox = () =>
    setBoxes((current) => [
      ...current,
      { key: `box-${nextBoxKey.current++}`, categoryId: null },
    ]);

  // Removing a box removes its lines too.
  const removeBox = (box: CuttingCategoryBox) => {
    if (box.indexes.length > 0) removeLines(box.indexes);

    setBoxes((current) => current.filter((item) => item.key !== box.key));
  };

  // A box picks its category while it has no lines.
  const changeBoxCategory = (key: string, categoryId: number | null) =>
    setBoxes((current) =>
      current.map((box) => (box.key === key ? { ...box, categoryId } : box))
    );

  // Back to one empty box, once the form's lines are gone.
  const resetBoxes = () =>
    setBoxes([{ key: `box-${nextBoxKey.current++}`, categoryId: null }]);

  // Why the boxes can't be saved, as the toast says it: every box needs a category and an item of it. There is always a
  // box, so a form without lines stops here.
  const boxProblem = () => {
    const index = categoryBoxes.findIndex(
      (box) => box.categoryId === null || box.indexes.length === 0
    );

    if (index === -1) return null;

    return `કેટેગરી ${index + 1}: ${
      categoryBoxes[index].categoryId === null
        ? "કેટેગરી પસંદ કરો"
        : "આ કેટેગરી ની ઓછામાં ઓછી એક આઈટમ ઉમેરો"
    }`;
  };

  return {
    boxes: categoryBoxes,
    addBox,
    removeBox,
    changeBoxCategory,
    resetBoxes,
    boxProblem,
  };
};

type Props = {
  boxes: CuttingCategoryBox[];
  // The categories the select offers, and every category's name for the box titles.
  categoryOptions: CuttingCategoryOption[];
  categoryLabel: (categoryId: number | null) => string | null;
  // What the category select says when it has nothing to offer.
  categoriesMessage: string;
  // Whether to show why a box can't be saved (after the first save).
  showBoxErrors: boolean;
  // A line that can't be taken off (it has received pieces) keeps its box.
  isLocked: (index: number) => boolean;
  canPick: boolean;
  pickerLabel: string;
  onAddBox: () => void;
  onRemoveBox: (box: CuttingCategoryBox) => void;
  onChangeBoxCategory: (key: string, categoryId: number | null) => void;
  onOpenPicker: (categoryId: number) => void;
  // One line's row, with its own key: its index in the form's items, its place in its box (from 1), and how it opens
  // its box's picker.
  renderRow: (
    index: number,
    number: number,
    openPicker: () => void
  ) => React.ReactNode;
};

const CuttingCategoryBoxes = ({
  boxes,
  categoryOptions,
  categoryLabel,
  categoriesMessage,
  showBoxErrors,
  isLocked,
  canPick,
  pickerLabel,
  onAddBox,
  onRemoveBox,
  onChangeBoxCategory,
  onOpenPicker,
  renderRow,
}: Props) => {
  const freeCategories = categoryOptions.filter(
    (option) => !boxes.some((box) => box.categoryId === option.value)
  ).length;
  const emptyBoxes = boxes.filter((box) => box.categoryId === null).length;
  const canAddBox = canPick && freeCategories > emptyBoxes;

  return (
    <div className="space-y-4">
      <label className={fieldLabelClass}>
        કેટેગરીઓ અને આઈટમ્સ <span className="text-red-500">*</span>
      </label>

      {boxes.map((box, boxIndex) => {
        const label = categoryLabel(box.categoryId);
        const hasLines = box.indexes.length > 0;
        const removable =
          boxes.length > 1 && box.indexes.every((index) => !isLocked(index));
        const categoryError = showBoxErrors && box.categoryId === null;
        const linesError =
          showBoxErrors && box.categoryId !== null && !hasLines;
        const inputId = `cutting-category-${box.key}`;
        const openPicker = () =>
          box.categoryId !== null && onOpenPicker(box.categoryId);

        return (
          <div
            key={box.key}
            className="space-y-4 rounded-xl border-2 border-gray-200 bg-white p-4"
          >
            <div className="flex items-center justify-between gap-2 rounded-lg bg-gradient-to-r from-[#113F67] to-[#34699A] px-4 py-2.5 text-white">
              <span className="min-w-0 truncate text-base font-semibold">
                કેટેગરી {boxIndex + 1}
                {label && `: ${label}`}
              </span>
              {removable && (
                <button
                  type="button"
                  title="કેટેગરી કાઢી નાખો"
                  aria-label={`કેટેગરી ${boxIndex + 1} કાઢી નાખો`}
                  className="shrink-0 rounded-md p-1.5 text-white/80 hover:bg-white/15 hover:text-white"
                  onClick={() => onRemoveBox(box)}
                >
                  <LuTrash2 className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor={inputId} className={fieldLabelClass}>
                કેટેગરી પસંદ કરો <span className="text-red-500">*</span>
              </label>
              <div
                className={cx("relative", categoryError && "error")}
                title={
                  hasLines
                    ? "આ કેટેગરી ની આઈટમ કાઢ્યા પછી કેટેગરી બદલી શકાશે"
                    : undefined
                }
              >
                <LuTag className={formSelectIconClass} />
                <Select
                  inputId={inputId}
                  options={categoryOptions}
                  value={
                    box.categoryId !== null && label
                      ? { value: box.categoryId, label }
                      : null
                  }
                  isOptionDisabled={(option: any) =>
                    option.value !== box.categoryId &&
                    boxes.some((other) => other.categoryId === option.value)
                  }
                  isDisabled={hasLines}
                  onChange={(option: any) =>
                    onChangeBoxCategory(box.key, option?.value ?? null)
                  }
                  placeholder="કેટેગરી સિલેક્ટ કરો..."
                  noOptionsMessage={() => categoriesMessage}
                  styles={iconSelectStyles}
                  isSearchable
                  className={cx(
                    "react-select-container",
                    categoryError && "error"
                  )}
                  classNamePrefix="react-select"
                />
              </div>
              {categoryError && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  કેટેગરી પસંદ કરો
                </p>
              )}
            </div>

            {box.indexes.map((index, position) =>
              renderRow(index, position + 1, openPicker)
            )}

            {linesError && (
              <p className={fieldErrorTextClass}>
                <LuAlertCircle className="h-4 w-4 mr-1" />
                આ કેટેગરી ની ઓછામાં ઓછી એક આઈટમ ઉમેરો
              </p>
            )}

            <button
              type="button"
              className={arrayAddButtonClass}
              disabled={!canPick || box.categoryId === null}
              title={
                !canPick
                  ? pickerLabel
                  : box.categoryId === null
                  ? "પહેલા કેટેગરી પસંદ કરો"
                  : undefined
              }
              onClick={openPicker}
            >
              <LuPlus className="h-4 w-4" />
              નવું આઈટમ ઉમેરો
            </button>
          </div>
        );
      })}

      <button
        type="button"
        className={arrayAddButtonClass}
        disabled={!canAddBox}
        title={
          !canPick
            ? pickerLabel
            : !canAddBox
            ? "બીજી કોઈ કેટેગરી બાકી નથી"
            : undefined
        }
        onClick={onAddBox}
      >
        <LuPlus className="h-4 w-4" />
        નવી કેટેગરી ઉમેરો
      </button>
    </div>
  );
};

export default CuttingCategoryBoxes;
